import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert, X } from 'lucide-react';
import { Z_INDEX } from '../../utils/constants';

const TONE_STYLES = {
  default: {
    iconWrap: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    Icon: CheckCircle2,
    primary:
      'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed',
    stepActive: 'bg-blue-600 text-white',
    stepIdle: 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
  },
  warning: {
    iconWrap: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    Icon: AlertTriangle,
    primary:
      'bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed',
    stepActive: 'bg-orange-600 text-white',
    stepIdle: 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
  },
  danger: {
    iconWrap: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    Icon: ShieldAlert,
    primary:
      'bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed',
    stepActive: 'bg-red-600 text-white',
    stepIdle: 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
  },
};

/**
 * İki adımlı onay penceresi — buton içi "Emin misiniz?" yerine ayrı dialog.
 */
const TwoStepConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'İşlemi onayla',
  step1Description = 'Bu işlemi yapmak istediğinize emin misiniz?',
  step2Description = 'Son onay: işlemi uygulamak için tekrar onaylayın. Bu adım geri alınamayabilir.',
  continueLabel = 'Devam',
  confirmLabel = 'Evet, Onayla',
  cancelLabel = 'İptal',
  tone = 'default',
  busy = false,
  busyLabel = 'İşleniyor...',
  /** Tek adım: uyarıyı gösterip doğrudan onaylatır (adım göstergesi yok) */
  singleStep = false,
}) => {
  const [step, setStep] = useState(1);
  const styles = TONE_STYLES[tone] || TONE_STYLES.default;
  const Icon = styles.Icon;
  const isFinalStep = singleStep || step === 2;

  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, busy]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center animate-in fade-in p-4 pt-[max(1rem,env(safe-area-inset-top))] overflow-y-auto"
      style={{ zIndex: Z_INDEX.confirmModal }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="two-step-confirm-title"
        className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 dark:border-slate-800 flex flex-col transition-colors my-auto"
      >
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 shrink-0">
          <h3
            id="two-step-confirm-title"
            className="font-bold text-base sm:text-lg text-slate-800 dark:text-white pr-2"
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            aria-label="Kapat"
            className="p-2 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 rounded-full transition-colors shadow-sm disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          {!singleStep && (
            <div className="flex items-center justify-center gap-2" aria-label={`Adım ${step} / 2`}>
              {[1, 2].map((n) => (
                <React.Fragment key={n}>
                  <span
                    className={`w-8 h-8 rounded-full text-xs font-black flex items-center justify-center ${
                      step === n ? styles.stepActive : styles.stepIdle
                    }`}
                  >
                    {n}
                  </span>
                  {n === 1 && (
                    <span className="w-8 h-0.5 bg-slate-200 dark:bg-slate-700 rounded-full" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="text-center space-y-3">
            <div
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto ${styles.iconWrap}`}
            >
              <Icon size={36} />
            </div>
            {!singleStep && (
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Adım {step} / 2
              </p>
            )}
            <div className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
              {step === 1 ? step1Description : step2Description}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={step === 1 ? handleClose : () => setStep(1)}
              disabled={busy}
              className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 py-3.5 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
            >
              {step === 1 ? cancelLabel : 'Geri'}
            </button>
            {!isFinalStep ? (
              <button
                type="button"
                onClick={() => setStep(2)}
                className={`flex-1 py-3.5 rounded-xl font-bold transition-colors ${styles.primary}`}
              >
                {continueLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                className={`flex-1 py-3.5 rounded-xl font-bold transition-colors ${styles.primary}`}
              >
                {busy ? busyLabel : confirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoStepConfirmModal;
